import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
  // Eliminando importaciones no utilizadas:
  // EventNote as EventNoteIcon,
  // Event as EventIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import sesionService from '../../services/sesionService';
// Corregir la ruta de importación - importar desde components/historias en lugar de ./
import SesionForm from '../../components/historias/SesionForm';
import FiltroCitasPorEtapa from '../../components/citas/FiltroCitasPorEtapa';

const SesionList = ({ historiaId, onRefresh }) => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [sesiones, setSesiones] = useState([]);
  const [selectedSesion, setSelectedSesion] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmRealizadaDialogOpen, setConfirmRealizadaDialogOpen] = useState(false);
  const [confirmNoAsistioDialogOpen, setConfirmNoAsistioDialogOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingSesionId, setEditingSesionId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [etapasFiltradas, setEtapasFiltradas] = useState([]);
  const [historia, setHistoria] = useState(null); // Definiendo historia como estado
  
  // Cargar sesiones
  useEffect(() => {
    const fetchSesiones = async () => {
      setLoading(true);
      try {
        const response = await sesionService.getSesionesByHistoria(historiaId);
        setSesiones(response.data || []);
      } catch (error) {
        console.error('Error al cargar sesiones:', error);
        showNotification('Error al cargar las sesiones', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    if (historiaId) {
      fetchSesiones();
    }
  }, [historiaId, refreshKey, showNotification]);

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  // Formatear hora
  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      const [hours, minutes] = timeString.split(':');
      const date = new Date();
      date.setHours(parseInt(hours, 10));
      date.setMinutes(parseInt(minutes, 10));
      return format(date, 'h:mm a', { locale: es });
    } catch (error) {
      return timeString;
    }
  };

  // Obtener color del chip según estado
  const getStatusColor = (estado) => {
    switch (estado) {
      case 'Programada':
        return 'primary';
      case 'Realizada':
        return 'success';
      case 'Cancelada':
        return 'error';
      case 'No asistió':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Mostrar formulario de nueva sesión
  const handleNuevaSesion = () => {
    setEditingSesionId(null);
    setShowForm(true);
  };

  // Editar sesión
  const handleEditSesion = (sesionId) => {
    setEditingSesionId(sesionId);
    setShowForm(true);
  };

  // Cancelar creación/edición
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingSesionId(null);
  };

  // Guardar sesión (nueva o editada)
  const handleSaveSesion = () => {
    setShowForm(false);
    setEditingSesionId(null);
    setRefreshKey(prev => prev + 1);
    
    if (onRefresh && typeof onRefresh === 'function') {
      onRefresh();
    }
  };

  // Ver detalles de sesión
  const handleViewSesion = (sesion) => {
    setSelectedSesion(sesion);
    setViewDialogOpen(true);
  };

  // Confirmar eliminación
  const handleDeleteClick = (sesion) => {
    setSelectedSesion(sesion);
    setDeleteDialogOpen(true);
  };

  // Eliminar sesión
  const handleConfirmDelete = async () => {
    try {
      await sesionService.deleteSesion(selectedSesion.id);
      showNotification('Sesión eliminada correctamente', 'success');
      setDeleteDialogOpen(false);
      setRefreshKey(prev => prev + 1);
      
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (error) {
      console.error('Error al eliminar sesión:', error);
      showNotification('Error al eliminar la sesión', 'error');
    }
  };

  // Abrir diálogo para marcar como realizada
  const handleMarcarRealizadaClick = (sesion) => {
    setSelectedSesion(sesion);
    setConfirmRealizadaDialogOpen(true);
  };

  // Marcar sesión como realizada
  const handleConfirmRealizada = async () => {
    try {
      await sesionService.marcarSesionRealizada(selectedSesion.id, { 
        notas: selectedSesion.notas 
      });
      showNotification('Sesión marcada como realizada correctamente', 'success');
      setConfirmRealizadaDialogOpen(false);
      setRefreshKey(prev => prev + 1);
      
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (error) {
      console.error('Error al marcar sesión como realizada:', error);
      showNotification('Error al marcar la sesión como realizada', 'error');
    }
  };

  // Abrir diálogo para marcar como no asistió
  const handleMarcarNoAsistioClick = (sesion) => {
    setSelectedSesion(sesion);
    setConfirmNoAsistioDialogOpen(true);
  };

  // Marcar sesión como no asistió
  const handleConfirmNoAsistio = async () => {
    try {
      await sesionService.marcarSesionNoAsistio(selectedSesion.id);
      showNotification('Sesión marcada como no asistió correctamente', 'success');
      setConfirmNoAsistioDialogOpen(false);
      setRefreshKey(prev => prev + 1);
      
      if (onRefresh && typeof onRefresh === 'function') {
        onRefresh();
      }
    } catch (error) {
      console.error('Error al marcar sesión como no asistió:', error);
      showNotification('Error al marcar la sesión como no asistió', 'error');
    }
  };
  
  // Definir programaId usando estado de historia
  const programaId = historia?.programa_id || null;

  // Sesiones filtradas - usando el estado de sesiones
  const sesionesFiltradas = sesiones.filter(sesion => {
    // Filtrar por etapa si hay etapas seleccionadas
    if (etapasFiltradas.length > 0 && !etapasFiltradas.includes(sesion.etapa_id)) {
      return false;
    }
    
    // Resto de los filtros existentes...
    return true;
  });

  // Ordenar sesiones: primero las programadas, luego por fecha
  const sortedSesiones = [...sesiones].sort((a, b) => {
    // Primero ordenar por estado (programadas/confirmadas primero)
    if (a.estado === 'Programada' && b.estado !== 'Programada') return -1;
    if (a.estado !== 'Programada' && b.estado === 'Programada') return 1;
    
    // Luego ordenar por fecha (más recientes primero)
    return new Date(b.fecha) - new Date(a.fecha);
  });

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Sesiones
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          {/* Filtro por etapa */}
          <FiltroCitasPorEtapa
            programaId={programaId}
            value={etapasFiltradas}
            onChange={setEtapasFiltradas}
          />
          
          {/* Otros filtros existentes */}
        </Box>
        {!showForm && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNuevaSesion}
            size="small"
          >
            Nueva Sesión
          </Button>
        )}
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircularProgress />
        </Box>
      ) : showForm ? (
        <SesionForm 
          historiaId={historiaId}
          sesionId={editingSesionId}
          onSave={handleSaveSesion}
          onCancel={handleCancelForm}
        />
      ) : sesiones.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary" gutterBottom>
            No hay sesiones registradas para esta historia clínica.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNuevaSesion}
            sx={{ mt: 2 }}
          >
            Registrar Nueva Sesión
          </Button>
        </Paper>
      ) : (
        <>
          {/* Sesiones programadas en cards */}
          {sortedSesiones.filter(s => s.estado === 'Programada').length > 0 && (
            <Box mb={3}>
              <Typography variant="subtitle1" gutterBottom>
                Próximas Sesiones
              </Typography>
              
              <Grid container spacing={2}>
                {sortedSesiones
                  .filter(s => s.estado === 'Programada')
                  .map(sesion => (
                    <Grid item xs={12} sm={6} md={4} key={sesion.id}>
                      <Card sx={{ height: '100%', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                            <Typography variant="subtitle1" fontWeight="medium">
                              {formatDate(sesion.fecha)}
                            </Typography>
                            <Chip 
                              label={sesion.estado} 
                              size="small" 
                              color="primary"
                            />
                          </Box>
                          
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            {formatTime(sesion.hora_inicio)} - {formatTime(sesion.hora_fin)}
                          </Typography>
                          
                          <Typography variant="body2">
                            <strong>Especialista:</strong> {sesion.especialista?.usuario?.nombre} {sesion.especialista?.usuario?.apellidos}
                          </Typography>
                          
                          {sesion.notas && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              {sesion.notas}
                            </Typography>
                          )}
                        </CardContent>
                        
                        <Box display="flex" justifyContent="flex-end" p={1}>
                          <Tooltip title="Marcar como realizada">
                            <IconButton 
                              size="small" 
                              color="success" 
                              onClick={() => handleMarcarRealizadaClick(sesion)}
                              sx={{ mr: 1 }}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Marcar como no asistió">
                            <IconButton 
                              size="small" 
                              color="warning" 
                              onClick={() => handleMarcarNoAsistioClick(sesion)}
                              sx={{ mr: 1 }}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleEditSesion(sesion.id)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </Box>
          )}
          
          {/* El resto del componente sigue igual... */}
          {/* (código omitido para brevedad) */}
        </>
      )}

      {/* Diálogos... */}
      {/* (código de diálogos omitido para brevedad) */}
    </>
  );
};

export default SesionList;